import { createSelector } from 'reselect';
import { get, identity, omit, pickBy, reduce, some } from 'lodash';
import { replaceInnerHTML } from '../../lib/dom.utils';
import { currentUserSelector } from '../authentication';
import { hasPermissions, PERMISSIONS } from '../../constants/authentication/permissions';
import { getRandomInt } from '../../lib/number.utils';
import { CONTRACT_STATE_SENT, CONTRACT_STATE_SENT_DRAFT, CONTRACT_TYPES } from '../../constants/settings';
import { toDisplayString } from '../../lib/date.utils';
import { bufferArrayToUtf8 } from '../../lib/string.utils';

const contract = state => state.contracts.review.contract;
const review = state => state.contracts.review;

// CONTRACTS REVIEW
export const fetchingSelector = createSelector(review, i => i.fetching);
export const submitMessageSelector = createSelector(review, i => i.submitMessage);
export const fetchedSelector = createSelector(review, i => i.fetched);
export const commentingSelector = createSelector(review, i => i.commenting);
export const commentsSelector = createSelector(review, r => r.comments);
export const hasComments = createSelector(commentsSelector, comments => {
    return reduce(
        pickBy(comments, (v, k) => k !== 'admin'),
        (res, val) => res || !!val,
        false
    );
});
export const getUserFromVersion = createSelector(
    contract,
    c => (c.version && c.version.user && c.version.user.fullName) || (c.version && c.version.anonName) || ''
);

export const currentCommentSelector = createSelector(review, r => r.currentComment);
export const savedCommentSelector = createSelector(commentingSelector, commentsSelector, (t, c) =>
    t && t.id ? c[t.id] || '' : ''
);
export const signedSelector = createSelector(review, r => r.signed);
export const isSignedSelector = createSelector(contract, c => c.isSigned);
export const isUploadSelector = createSelector(contract, c => get(c, 'version.isUpload'));

export const signNameSelector = createSelector(review, r => r.signName);
export const signEmailSelector = createSelector(review, r => r.signEmail);
export const signFunctionSelector = createSelector(review, r => r.signFunction);
export const submittingSelector = createSelector(review, r => r.submitting);

export const canSubmitSelector = createSelector(review, currentUserSelector, (r, user) => {
    const isLoggedIn = !!user && !!user.id;
    if (!isLoggedIn) {
        return {
            sign: r.signed && r.signName && r.signEmail,
            comments: r.signName && r.signEmail && some(omit(r.comments, 'admin'), identity)
        };
    }
    return { sign: r.signed && r.signName, comments: some(omit(r.comments, 'admin'), identity) };
});

export const currentVersionIsLastVersion = (contract, isPublic) => {
    const versions = contract.versions || [{}];
    const currentVersion = contract.version || {};
    return versions[0].id === currentVersion.id || isPublic;
};

export const isReviewableState = (state, contract, isPublic) => {
    return (
        (state === CONTRACT_STATE_SENT || state === CONTRACT_STATE_SENT_DRAFT) &&
        currentVersionIsLastVersion(contract, isPublic)
    );
};

export const contractSelector = createSelector(contract, c => c);
export const stateSelector = createSelector(contract, c => get(c, 'version.state'));
export const userNameSelector = createSelector(contract, c => get(c, 'signedBy'));
export const signedOnSelector = createSelector(contract, c => c.signedOn);
export const contractTextSelector = createSelector(contract, currentUserSelector, (c, user) => {
    const text = get(c, 'version.data') || '';
    const state = get(c, 'version.state');
    const regexInput = /<ipf .*?>.*?<\/ipf>/g;
    return text
        .replace(/<input/g, '&zwnj;<input')
        .replace(/contenteditable/g, 'contentnoteditable')
        .replace(/ce-element--empty/g, '')
        .replace(/ce-element--focused/g, '')
        .replace(regexInput, match => {
            const contentRegex = />(.*?)<\/ipf>/g;
            const value = match.match(contentRegex)[0].replace('</ipf>', '').substring(1);
            return replaceInnerHTML(
                'ipf',
                `<input value="${value === 'INPUT FIELD' ? '' : value}" id="rte_input_${getRandomInt(
                    100000
                )}_${Date.now()}" ${
                    hasPermissions(user, PERMISSIONS.SIGN_CONTRACTS_INBOX) && state === CONTRACT_STATE_SENT
                        ? ''
                        : 'disabled'
                } type="text" />`,
                match,
                { html: true }
            );
        });
});
export const reviewDataSelector = createSelector(review, r => r);

export const contractTextFeedback = (node, signed, contract, signedBy, signedFunction, signatureImage) => {
    const eventFieldsPrefix = contract.type === CONTRACT_TYPES.PERFORMANCE ? 'performance' : 'default';
    const texts = node.querySelectorAll('input[type=text]');
    let valid = true;
    texts.forEach(t => {
        if (t.id && t.id.startsWith('CLR_REQ_')) {
            valid = valid && !!t.value;
            t.classList.toggle('ckp-contracts-review--required', !t.value);
        }
        t.setAttribute('value', t.value);
    });
    let transformedText = node.innerHTML;
    if (signed) {
        const regexSignatures = /<\s*sgn (.*?)>(.*?)<\s*\/s*sgn>/g;
        const regexEventSignature = /<\s*sgne (.*?)>(.*?)<\s*\/s*sgne>/g;
        const regexArtistSignature = /<\s*sgna (.*?)>(.*?)<\s*\/s*sgna>/g;

        const hasSignaturesPlaceholder = transformedText.match(regexSignatures);
        const hasEventSignaturePlaceholder = transformedText.match(regexEventSignature);
        const hasArtistSignaturePlaceholder = transformedText.match(regexArtistSignature);

        const artistSignature = _htmlArtistSignature(signedBy, signedFunction, signatureImage);
        const eventSignature = _htmlEventSignature(contract, eventFieldsPrefix);
        const htmlSignatures = `<div class='ckp-contract-signatures'>
            ${artistSignature}${eventSignature}</div>`;

        if (hasSignaturesPlaceholder || hasEventSignaturePlaceholder || hasArtistSignaturePlaceholder) {
            transformedText = transformedText.replace(regexSignatures, () => htmlSignatures);
            transformedText = transformedText.replace(regexEventSignature, () => eventSignature);
            transformedText = transformedText.replace(regexArtistSignature, () => artistSignature);
        } else {
            transformedText = transformedText + htmlSignatures;
        }
    }
    return { html: transformedText.replace(/&zwnj;/g, ''), valid };
};

function _htmlArtistSignature(signedBy, signedFunction, signatureImage) {
    return `<p>Digitally signed by <strong>${signedBy || ''}${
        signedFunction ? ` (${signedFunction})` : ''
    }</strong> on <strong>${toDisplayString()}</strong>
    <img class='ckp-contract-signature' src="${signatureImage}"></p>`;
}

function _htmlEventSignature(contract, eventFieldsPrefix) {
    const signerFunction = get(contract, `event.${eventFieldsPrefix}SignerFunction`) || '';
    return `<p>Signature on behalf of <strong>${get(contract, 'event.name')}</strong> - <strong>${
        get(contract, `event.${eventFieldsPrefix}Signer`) || ''
    }${
        signerFunction ? ` (${signerFunction})` : ''
    }</strong><img class='ckp-contract-signature' src="${bufferArrayToUtf8(
        get(contract, `event.${eventFieldsPrefix}Signature.data`) || []
    )}"></p>`;
}
